import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { washService, customerService, firestoreService } from '../../services/firebase'
import { Car, Star, Calendar, Filter } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import BottomNav from '../../components/BottomNav'
import CustomDropdown from '../../components/CustomDropdown'
import PageHeader from '../../components/PageHeader'
import StatTile from '../../components/StatTile'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

function getDateFromTimestamp(timestamp) {
  if (!timestamp) return new Date()
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate()
  }
  return new Date(timestamp)
}

function statusBadgeClass(status) {
  switch (status) {
    case 'completed':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
    case 'ready':
      return 'border-sky-500/30 bg-sky-500/10 text-sky-900 dark:text-sky-300'
    case 'drying':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-300'
    case 'washing':
      return 'border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-300'
    case 'waiting':
      return 'border-border bg-muted text-muted-foreground'
    default:
      return 'border-border bg-muted text-muted-foreground'
  }
}

export default function CustomerHistory() {
  const { currentUser } = useAuth()
  const [washes, setWashes] = useState([])
  const [filteredWashes, setFilteredWashes] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [selectedPeriod, setSelectedPeriod] = useState('all')
  const [branchNames, setBranchNames] = useState({})

  useEffect(() => {
    async function fetchWashHistory() {
      if (!currentUser) {
        setLoading(false)
        return
      }

      try {
        await customerService.ensureCustomerProfileForUser(currentUser)
        const ids = await customerService.listCustomerIdsForUser(currentUser)
        const list = await washService.getCustomerWashesByCustomerIds(ids)
        setWashes(list)
      } catch (error) {
        console.error('Error fetching wash history:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchWashHistory()
  }, [currentUser])

  useEffect(() => {
    let filtered = [...washes]

    if (selectedFilter !== 'all') {
      filtered = filtered.filter((wash) => wash.status === selectedFilter)
    }

    if (selectedPeriod !== 'all') {
      const now = new Date()
      const periodStart = new Date()

      switch (selectedPeriod) {
        case 'week':
          periodStart.setDate(now.getDate() - 7)
          break
        case 'month':
          periodStart.setMonth(now.getMonth() - 1)
          break
        case 'quarter':
          periodStart.setMonth(now.getMonth() - 3)
          break
        default:
          break
      }

      filtered = filtered.filter((wash) => getDateFromTimestamp(wash.createdAt) >= periodStart)
    }

    setFilteredWashes(filtered)
  }, [washes, selectedFilter, selectedPeriod])

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (washes || [])
          .map((w) => w?.branchId)
          .filter((id) => !!id && id !== 'main-branch' && id !== 'all-branches')
      )
    )
    if (ids.length === 0) return

    let cancelled = false
    ;(async () => {
      try {
        const entries = await Promise.all(
          ids.map(async (id) => {
            try {
              const b = await firestoreService.getById('branches', id)
              return [id, b?.name || null]
            } catch {
              return [id, null]
            }
          })
        )
        if (cancelled) return
        setBranchNames((prev) => {
          const next = { ...prev }
          for (const [id, name] of entries) {
            if (name) next[id] = name
          }
          return next
        })
      } catch (e) {
        console.error('Failed to resolve branch names:', e)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [washes])

  const stats = useMemo(() => {
    const totalWashes = filteredWashes.length
    const withRating = filteredWashes.filter((w) => w.rating)
    const avgRating =
      withRating.length > 0
        ? withRating.reduce((sum, w) => sum + (w.rating || 0), 0) / withRating.length
        : 0

    const now = new Date()
    const thisMonth = washes.filter((w) => {
      const washDate = getDateFromTimestamp(w.createdAt)
      return washDate.getMonth() === now.getMonth() && washDate.getFullYear() === now.getFullYear()
    }).length

    return { totalWashes, avgRating, thisMonth }
  }, [filteredWashes, washes])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
        <PageHeader
          eyebrow="Your visits"
          title="Wash history"
          description="View past washes and ratings in one place."
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatTile label="Total washes" value={stats.totalWashes} icon={Car} tone="default" />
          <StatTile
            label="Avg. rating"
            value={stats.avgRating > 0 ? stats.avgRating.toFixed(1) : 'N/A'}
            icon={Star}
            tone="warning"
          />
          <StatTile label="This month" value={stats.thisMonth} icon={Calendar} tone="info" />
        </div>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <CardTitle className="flex items-center gap-2 text-base font-medium">
                <span className="flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Filter className="size-4" />
                </span>
                Filters
              </CardTitle>
              <div className="flex flex-col gap-3 sm:flex-row">
                <CustomDropdown
                  options={[
                    { value: 'all', label: 'All status' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'ready', label: 'Ready' },
                    { value: 'drying', label: 'Drying' },
                    { value: 'washing', label: 'Washing' },
                    { value: 'waiting', label: 'Waiting' },
                  ]}
                  value={selectedFilter}
                  onChange={setSelectedFilter}
                  placeholder="Status"
                  className="min-w-[160px]"
                />
                <CustomDropdown
                  options={[
                    { value: 'all', label: 'All time' },
                    { value: 'week', label: 'Last 7 days' },
                    { value: 'month', label: 'Last month' },
                    { value: 'quarter', label: 'Last 3 months' },
                  ]}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  placeholder="Period"
                  className="min-w-[160px]"
                />
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-4">
          {filteredWashes.length > 0 ? (
            filteredWashes.map((wash) => (
              <Card key={wash.id} className="border-border/80 shadow-sm transition-shadow hover:shadow-md">
                <CardContent className="pt-6">
                  <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{wash.serviceType}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {getDateFromTimestamp(wash.createdAt).toLocaleDateString()} ·{' '}
                        {getDateFromTimestamp(wash.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xl font-semibold tabular-nums text-foreground">
                        R{wash.amount?.toFixed(2)}
                      </p>
                      <Badge
                        variant="outline"
                        className={cn('mt-1 font-medium capitalize', statusBadgeClass(wash.status))}
                      >
                        {wash.status || 'unknown'}
                      </Badge>
                    </div>
                  </div>

                  <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Vehicle</p>
                      <p className="font-medium text-foreground">
                        {wash.vehicleDetails?.make} {wash.vehicleDetails?.model}
                      </p>
                      <p className="text-sm text-muted-foreground">{wash.vehicleDetails?.licensePlate}</p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Branch</p>
                      <p className="font-medium text-foreground">
                        {branchNames[wash.branchId] || wash.branchName || '—'}
                      </p>
                    </div>
                    {wash.staffName && (
                      <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                        <p className="text-xs font-medium text-muted-foreground">Staff</p>
                        <p className="font-medium text-foreground">{wash.staffName}</p>
                      </div>
                    )}
                    {wash.completedAt && (
                      <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                        <p className="text-xs font-medium text-muted-foreground">Duration</p>
                        <p className="font-medium text-foreground">
                          {Math.round(
                            (getDateFromTimestamp(wash.completedAt) -
                              getDateFromTimestamp(wash.createdAt)) /
                              (1000 * 60)
                          )}{' '}
                          min
                        </p>
                      </div>
                    )}
                  </div>

                  {wash.rating && (
                    <>
                      <Separator className="my-4" />
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                        <span className="text-sm font-medium text-foreground">Your rating</span>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                'size-5',
                                i < wash.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-muted-foreground/30'
                              )}
                            />
                          ))}
                          <span className="ml-2 text-sm text-muted-foreground">({wash.rating}/5)</span>
                        </div>
                      </div>
                    </>
                  )}

                  {wash.comments && (
                    <div className="mt-4 rounded-lg border border-border/60 bg-muted/20 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Comments</p>
                      <p className="mt-1 text-sm text-foreground">{wash.comments}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-dashed border-border/80 shadow-none">
              <CardContent className="flex flex-col items-center py-14 text-center">
                <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                  <Car className="size-8" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">No washes found</h3>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  Try different filters, or visit us for your first wash.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  )
}
